<template>
  <div class="adhesion-view">
    <b-container class="py-4">
      <b-row>
        <b-col cols="12" lg="10" class="mx-auto">
          <b-card class="adhesion-card mb-4">
            <div class="d-flex flex-wrap align-items-start justify-content-between">
              <div>
                <h3 class="mb-1">Mon adhésion</h3>
                <p class="text-muted mb-0">
                  Suivi de votre statut et gestion de l’adhésion famille.
                </p>
              </div>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mt-2 mt-md-0"
                :disabled="adhesionLoading"
                @click="fetchMyAdhesions"
              >
                <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                Actualiser
              </b-button>
            </div>

            <b-alert v-if="adhesionError" variant="danger" show class="mt-3 mb-0">
              {{ adhesionError }}
            </b-alert>
          </b-card>

          <div v-if="adhesionLoading" class="loading-panel text-center py-5">
            <b-spinner label="Chargement..."></b-spinner>
            <p class="text-muted mt-3 mb-0">Chargement de vos adhésions…</p>
          </div>

          <template v-else-if="fullAdhesions.length">
            <b-card
              v-for="adhesion in fullAdhesions"
              :key="adhesion._id"
              no-body
              class="adhesion-card adhesion-card--active mb-4"
            >
              <div class="adhesion-card__header">
                <div class="adhesion-card__heading">
                  <span class="adhesion-card__icon">
                    <b-icon icon="award-fill"></b-icon>
                  </span>
                  <div>
                    <span class="adhesion-card__eyebrow">Adhésion</span>
                    <h4 class="adhesion-card__title">{{ adhesionTypeLabel(adhesion) }}</h4>
                  </div>
                </div>
                <b-badge
                  pill
                  :variant="isDayAdhesionUsed(adhesion) ? 'danger' : 'success'"
                  class="status-badge"
                >
                  <b-icon
                    :icon="isDayAdhesionUsed(adhesion) ? 'x-circle-fill' : 'check-circle-fill'"
                    class="mr-1"
                  ></b-icon>
                  {{ isDayAdhesionUsed(adhesion) ? "Utilisée" : "Active" }}
                </b-badge>
              </div>

              <div class="adhesion-card__body">
                <div class="overview-grid">
                  <div class="overview-item">
                    <span class="overview-icon"><b-icon icon="person-fill"></b-icon></span>
                    <div class="overview-text">
                      <span class="overview-label">Adhérent</span>
                      <strong class="overview-value">{{ userFullName }}</strong>
                    </div>
                  </div>
                  <div class="overview-item">
                    <span class="overview-icon"><b-icon icon="calendar-event-fill"></b-icon></span>
                    <div class="overview-text">
                      <span class="overview-label">Validité</span>
                      <strong class="overview-value">{{ validityLabel(adhesion) }}</strong>
                    </div>
                  </div>
                  <div class="overview-item">
                    <span class="overview-icon"><b-icon icon="tag-fill"></b-icon></span>
                    <div class="overview-text">
                      <span class="overview-label">Tarif réduit</span>
                      <strong class="overview-value">{{ adhesion.reduite ? "Oui" : "Non" }}</strong>
                    </div>
                  </div>
                  <div class="overview-item">
                    <span class="overview-icon"><b-icon icon="credit-card-2-front-fill"></b-icon></span>
                    <div class="overview-text">
                      <span class="overview-label">Paiement</span>
                      <strong class="overview-value">{{ paymentTypeLabel(adhesion) }}</strong>
                    </div>
                  </div>
                </div>
                <template v-if="adhesion.adhesion_type === 'SAISON_FAMILLE'">
                <hr />
                <h5 class="mb-2">Partage adhésion famille</h5>
                <p class="text-muted mb-3">
                  Ajoutez un membre via son email (2 membres maximum).
                </p>

                <div class="mb-3">
                  <h6 class="mb-2">Membres associés</h6>
                  <div v-if="adherents(adhesion).length" class="chips-row">
                    <b-badge
                      v-for="user in adherents(adhesion)"
                      :key="user._id"
                      variant="light"
                      class="chip"
                    >
                      {{ formatAdherentLabel(user) }}
                    </b-badge>
                  </div>
                  <p v-else class="text-muted mb-0">Aucun membre associé.</p>
                </div>

                <b-alert
                  v-if="familyShareErrors[adhesion._id]"
                  variant="danger"
                  show
                  class="mb-3"
                >
                  {{ familyShareErrors[adhesion._id] }}
                </b-alert>
                <b-alert
                  v-if="familyShareSuccesses[adhesion._id]"
                  variant="success"
                  show
                  class="mb-3"
                >
                  {{ familyShareSuccesses[adhesion._id] }}
                </b-alert>

                <template v-if="!isFamilyShareFull(adhesion)">
                  <b-form-group label="Email du membre à ajouter" class="mb-3">
                    <b-form-input
                      v-model.trim="familyShareEmails[adhesion._id]"
                      type="email"
                      placeholder="ex: membre@example.com"
                      :disabled="familyShareLoadingId === adhesion._id"
                    />
                  </b-form-group>
                  <b-button
                    variant="primary"
                    :disabled="!canShareFamily(adhesion) || familyShareLoadingId === adhesion._id"
                    @click="submitFamilyShare(adhesion)"
                  >
                    <b-spinner
                      v-if="familyShareLoadingId === adhesion._id"
                      small
                      class="mr-2"
                    ></b-spinner>
                    Partager l'adhésion
                  </b-button>
                </template>
              </template>
              </div>
            </b-card>
          </template>

          <template v-if="usedDayAdhesions.length">
            <b-card
              v-for="adhesion in usedDayAdhesions"
              :key="`used-${adhesion._id}`"
              class="adhesion-card adhesion-card-compact mb-3"
            >
              <div class="compact-row">
                <b-badge
                  pill
                  variant="danger"
                  class="status-badge"
                >
                  Journée Utilisée
                </b-badge>
                <span><strong>Validité:</strong> {{ validityLabel(adhesion) }}</span>
                <span><strong>Date:</strong> {{ formatDate(adhesion.dateUtilisation) }}</span>
              </div>
            </b-card>
          </template>

          <template v-if="!adhesions.length && !adhesionLoading">
            <b-card class="adhesion-card mb-4">
              <h5 class="mb-2">Aucune adhésion active</h5>
              <p class="text-muted mb-0">
                Souscrivez une adhésion pour participer aux activités du club.
              </p>
            </b-card>
          </template>

          <b-alert v-if="showSubscriptionWidget || showApresJourneeWidget" show variant="warning" class="mb-4" style="background-color: var(--color-guc-yellow); border-color: var(--color-guc-yellow); border-radius: 0.8rem;">
            <div class="d-flex align-items-start">
              <b-icon icon="exclamation-triangle-fill" class="mr-3 mt-1 flex-shrink-0" font-scale="1.4" />
              <div>
                <strong>Informations importantes avant de procéder au paiement</strong>
                <ul class="mb-0 mt-2">
                  <li>
                    Vous devez utiliser <strong>la même adresse mail</strong> que celle de votre compte sur cette application
                    <span v-if="currentUserEmail"> : <strong>{{ currentUserEmail }}</strong></span>.
                    Cela permet de lier automatiquement votre adhésion à votre compte.
                  </li>
                  <li class="mt-1">
                    HelloAsso ajoute <strong>par défaut un don</strong> pour leur fonctionnement. Vous pouvez le supprimer ou le modifier manuellement au récapitulatif de la commande, avant de valider le paiement.
                  </li>
                  <li class="mt-1">
                    Après le paiement, <strong>rafraîchissez régulièrement cette page</strong> et patientez jusqu'à <strong>5 minutes</strong> pour que votre adhésion soit prise en compte. Si ce n'est pas le cas passé ce délai, contactez le bureau.
                  </li>
                </ul>
              </div>
            </div>
          </b-alert>

          <template v-if="showApresJourneeWidget">
            <b-card class="adhesion-card">
              <h5 class="mb-2">Adhésion saison — tarif après journée</h5>
              <p class="text-muted mb-3">
                Vous avez déjà une adhésion journée. Ces tarifs réduits vous permettent de passer à une adhésion saison.
              </p>
              <b-tabs content-class="mt-3">
                <b-tab title="Saison normale">
                  <iframe
                    id="haWidget-saison-apres-journee"
                    allowtransparency="true"
                    
                    src="https://www.helloasso.com/associations/guc-voile/adhesions/adhesion-saison-apres-adhesion-journee/widget"
                    title="Widget HelloAsso adhésion saison normale après journée"
                    style="width: 100%; height: 750px; border: none;"
                    onload="window.addEventListener('message', function(e) { const dataHeight = e.data.height; const el = document.getElementById('haWidget-saison-apres-journee'); if (dataHeight > parseFloat(el.height || 0)) { el.height = dataHeight + 'px'; } })"
                  ></iframe>
                </b-tab>
                <b-tab title="Saison réduite">
                  <iframe
                    id="haWidget-reduite-apres-journee"
                    allowtransparency="true"
                    
                    src="https://www.helloasso.com/associations/guc-voile/adhesions/adhesion-saison-reduite-apres-adhesion-journee/widget"
                    title="Widget HelloAsso adhésion saison réduite après journée"
                    style="width: 100%; height: 750px; border: none;"
                    onload="window.addEventListener('message', function(e) { const dataHeight = e.data.height; const el = document.getElementById('haWidget-reduite-apres-journee'); if (dataHeight > parseFloat(el.height || 0)) { el.height = dataHeight + 'px'; } })"
                  ></iframe>
                </b-tab>
              </b-tabs>
            </b-card>
          </template>

          <template v-if="showSubscriptionWidget">
            <b-card class="adhesion-card">
              <b-tabs content-class="mt-3">
                <b-tab title="Saison normale">
                  <iframe
                    id="haWidget-saison"
                    allowtransparency="true"
                    
                    src="https://www.helloasso.com/associations/guc-voile/adhesions/adhesion-saison/widget"
                    title="Widget HelloAsso adhésion saison normale"
                    style="width: 100%; height: 750px; border: none;"
                    onload="window.addEventListener('message', function(e) { const dataHeight = e.data.height; const el = document.getElementById('haWidget-saison'); if (dataHeight > parseFloat(el.height || 0)) { el.height = dataHeight + 'px'; } })"
                  ></iframe>
                </b-tab>
                <b-tab title="Saison réduite">
                  <iframe
                    id="haWidget-reduite"
                    allowtransparency="true"
                    
                    src="https://www.helloasso.com/associations/guc-voile/adhesions/adhesion-saison-reduite/widget"
                    title="Widget HelloAsso adhésion saison réduite"
                    style="width: 100%; height: 750px; border: none;"
                    onload="window.addEventListener('message', function(e) { const dataHeight = e.data.height; const el = document.getElementById('haWidget-reduite'); if (dataHeight > parseFloat(el.height || 0)) { el.height = dataHeight + 'px'; } })"
                  ></iframe>
                </b-tab>
                <b-tab title="Saison famille">
                  <iframe
                    id="haWidget-famille"
                    allowtransparency="true"
                    
                    src="https://www.helloasso.com/associations/guc-voile/adhesions/adhesion-saison-famille/widget"
                    title="Widget HelloAsso adhésion saison famille"
                    style="width: 100%; height: 750px; border: none;"
                    onload="window.addEventListener('message', function(e) { const dataHeight = e.data.height; const el = document.getElementById('haWidget-famille'); if (dataHeight > parseFloat(el.height || 0)) { el.height = dataHeight + 'px'; } })"
                  ></iframe>
                </b-tab>
                <b-tab title="Journée">
                  <iframe
                    id="haWidget-journee"
                    allowtransparency="true"
                    
                    src="https://www.helloasso.com/associations/guc-voile/adhesions/adhesion-journee/widget"
                    title="Widget HelloAsso adhésion journée"
                    style="width: 100%; height: 750px; border: none;"
                    onload="window.addEventListener('message', function(e) { const dataHeight = e.data.height; const el = document.getElementById('haWidget-journee'); if (dataHeight > parseFloat(el.height || 0)) { el.height = dataHeight + 'px'; } })"
                  ></iframe>
                </b-tab>
              </b-tabs>
            </b-card>
          </template>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import AdhesionService from "@/services/adhesion.service";
import session from "@/store/session";

export default {
  name: "Adhesion",
  data() {
    return {
      adhesions: [],
      adhesionLoading: false,
      adhesionError: "",
      familyShareEmails: {},
      familyShareLoadingId: null,
      familyShareErrors: {},
      familyShareSuccesses: {}
    };
  },
  mounted() {
    this.fetchMyAdhesions();
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateStr).toLocaleDateString(undefined, options);
    },
    formatAdherentLabel(user) {
      const name = [user.name, user.surname].filter(Boolean).join(" ").trim();
      if (name) return name;
      if (user.email) return user.email;
      return user._id;
    },
    async fetchMyAdhesions() {
      this.adhesionLoading = true;
      this.adhesionError = "";
      try {
        const res = await AdhesionService.listMine();
        const adhesions = res.data.adhesions;
        this.adhesions = adhesions.length ? [adhesions[adhesions.length - 1]] : [];
      } catch (error) {
        this.adhesionError = error.message || "Impossible de recuperer vos adhesions.";
      } finally {
        this.adhesionLoading = false;
      }
    },
    replaceAdhesion(updatedAdhesion) {
      this.adhesions = this.adhesions.map((item) =>
        item._id === updatedAdhesion._id ? updatedAdhesion : item
      );
    },
    adhesionTypeLabel(adhesion) {
      if (adhesion.adhesion_type === "SAISON_SIMPLE") return "Saison individuelle";
      if (adhesion.adhesion_type === "SAISON_FAMILLE") return "Saison famille";
      if (adhesion.adhesion_type === "JOURNEE") return "Journée";
      return adhesion.adhesion_type;
    },
    validityLabel(adhesion) {
      if (!adhesion.saison) return "-";
      return `${this.formatDate(adhesion.saison.debut_saison)} - ${this.formatDate(adhesion.saison.fin_saison)}`;
    },
    isDayAdhesionUsed(adhesion) {
      return adhesion.adhesion_type === "JOURNEE" && adhesion.used === true;
    },
    paymentTypeLabel(adhesion) {
      if (adhesion.paiement_type === "LIQUIDE") return "Liquide";
      if (adhesion.paiement_type === "PAYSSO") return "Payasso";
      if (adhesion.paiement_type === "CHEQUE") return "Chèque";
      if (adhesion.paiement_type === "VIREMENT") return "Virement";
      return adhesion.paiement_type;
    },
    adherents(adhesion) {
      if (!Array.isArray(adhesion.adherents)) return [];
      return adhesion.adherents;
    },
    isFamilyShareFull(adhesion) {
      return this.adherents(adhesion).length >= 2;
    },
    canShareFamily(adhesion) {
      const shareEmail = String(this.familyShareEmails[adhesion._id] || "").trim();
      return Boolean(
        adhesion._id &&
        !this.isFamilyShareFull(adhesion) &&
        shareEmail
      );
    },
    async submitFamilyShare(adhesion) {
      if (!this.canShareFamily(adhesion)) return;
      this.familyShareLoadingId = adhesion._id;
      this.$set(this.familyShareErrors, adhesion._id, "");
      this.$set(this.familyShareSuccesses, adhesion._id, "");
      try {
        const userToShareWithEmail = String(this.familyShareEmails[adhesion._id] || "").trim();
        const payload = {
          adhesionId: adhesion._id,
          userToShareWithEmail
        };
        const res = await AdhesionService.shareFamily(payload);

        if (res.data.success && res.data.adhesion) {
          this.replaceAdhesion(res.data.adhesion);
          this.$set(this.familyShareSuccesses, adhesion._id, "Adhésion partagée avec succès.");
          this.$set(this.familyShareEmails, adhesion._id, "");
        } else {
          this.$set(
            this.familyShareErrors,
            adhesion._id,
            res.data.message || "Impossible de partager l'adhésion."
          );
        }
      } catch (error) {
        this.$set(
          this.familyShareErrors,
          adhesion._id,
          error.message || "Impossible de partager l'adhésion."
        );
      } finally {
        this.familyShareLoadingId = null;
      }
    }
  },
  computed: {
    currentUserEmail() {
      return session.user && session.user.email ? session.user.email : null;
    },
    userFullName() {
      const user = session.user;
      if (!user) return "-";
      const fullName = [user.name, user.surname].filter(Boolean).join(" ").trim();
      return fullName || user.email || "-";
    },
    fullAdhesions() {
      return this.adhesions.filter((adhesion) => !this.isDayAdhesionUsed(adhesion));
    },
    usedDayAdhesions() {
      return this.adhesions.filter((adhesion) => this.isDayAdhesionUsed(adhesion));
    },
    hasUsedJournee() {
      return this.usedDayAdhesions.length > 0;
    },
    showSubscriptionWidget() {
      return this.fullAdhesions.length === 0 && !this.hasUsedJournee;
    },
    showApresJourneeWidget() {
      return this.hasUsedJournee && this.fullAdhesions.length === 0;
    }
  }
};
</script>

<style scoped>
.adhesion-view {
  min-height: 60vh;
}

.adhesion-card {
  border: 1px solid #e9edf3;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(25, 35, 52, 0.06);
  overflow: hidden;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}

.adhesion-card--active:hover {
  box-shadow: 0 18px 42px rgba(25, 35, 52, 0.12);
  transform: translateY(-2px);
}

.adhesion-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.5rem;
  background: linear-gradient(135deg, var(--color-guc-darkblue) 0%, var(--color-guc-darkdarkblue) 100%);
  color: #ffffff;
}

.adhesion-card__heading {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
}

.adhesion-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.18);
  font-size: 1.4rem;
  flex-shrink: 0;
}

.adhesion-card__eyebrow {
  display: block;
  font-size: 0.72rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.85;
}

.adhesion-card__title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.2;
}

.adhesion-card__body {
  padding: 1.5rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.85rem;
  font-size: 0.78rem;
  font-weight: 600;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.18);
  flex-shrink: 0;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.overview-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: #f8fafc;
  border: 1px solid #ebf0f6;
  border-radius: 0.75rem;
  padding: 0.85rem 1rem;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.overview-item:hover {
  background: var(--color-guc-lightblue);
  border-color: var(--color-guc-blue);
}

.overview-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.65rem;
  background: var(--color-guc-blue);
  color: var(--color-guc-darkdarkblue);
  font-size: 1.05rem;
  flex-shrink: 0;
}

.overview-text {
  min-width: 0;
}

.overview-label {
  display: block;
  font-size: 0.74rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.15rem;
}

.overview-value {
  display: block;
  color: #1f2937;
  font-size: 0.98rem;
  font-weight: 600;
  word-break: break-word;
}

.chips-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.chip {
  padding: 0.45rem 0.7rem;
  border: 1px solid #e4e9f1;
  border-radius: 999px;
}

.adhesion-card-compact {
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;
}

.compact-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  align-items: center;
  font-size: 0.92rem;
}

.compact-pill {
  display: inline-flex;
  align-items: center;
  background: #fef3c7;
  color: #92400e;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.78rem;
  font-weight: 600;
}

@media (max-width: 767px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }

  .adhesion-card__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.85rem;
  }
}
</style>
